import { CheckmarkDoneCheck4Icon } from '@qitech/icons';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { Button } from '../components/Button';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { LoadingPage } from '../../../LoadingPage';
import * as S from './styles';
import { useEffect, useState } from 'react';
import axios from 'axios';
import JSZip from 'jszip';

interface Document {
    id: string;
    name: string;
    url: string;
    document_type: string;
}

interface SignedPageProps {
    signData: any;
}

export const SocialSecurityBenefitCardSignedPage = ({
    signData,
}: SignedPageProps) => {
    const { clientThemeSettings } = useThemeContext();

    const accentColor = clientThemeSettings?.font_color || '#0e3573';

    const [isDownloading, setIsDownloading] = useState(false);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const config = { headers: { Authorization: signData.signer_token } };
        axios.get('/signer/documents', config).then((response) => {
            setDocuments(response.data.documents ?? []);
        }).finally(() => {
            setLoading(false);
        });
    }, [signData.signer_token]);

    if (loading) {
        return <LoadingPage clientThemeSettings={clientThemeSettings} />;
    }

    const hasAcceptedCard = documents.length > 1;

    const isIOS = (/iPhone|iPad|iPod/i.test(navigator.userAgent));


    const handleDownloadDocuments = async () => {
        setIsDownloading(true);
        try {
            const zip = new JSZip();
            await Promise.all(
                documents.map(async (doc) => {
                    const response = await fetch(doc.url);
                    const blob = await response.blob();
                    zip.file(`${doc.name}.${doc.document_type}`, blob);
                })
            );
            const content = await zip.generateAsync({ type: 'blob' });
            const url = URL.createObjectURL(content);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'documentos.zip';
            a.click();
            URL.revokeObjectURL(url);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <S.PageWrapper>
            <S.ScrollContent>
                <S.Header>
                    {clientThemeSettings?.logo_url && (
                        <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                    )}
                </S.Header>

                <S.HeroSection>
                    <S.CheckIconWrapper $color={accentColor}>
                        <CheckmarkDoneCheck4Icon size={80} />
                    </S.CheckIconWrapper>

                    {hasAcceptedCard ? (
                        <>
                            <S.Title $accentColor={accentColor}>
                                Falta muito pouco para você começar a aproveitar todos os{' '}
                                <strong>benefícios do seu novo cartão.</strong>
                            </S.Title>
                            <S.Description>
                                Sua solicitação já está em análise. Em breve entraremos em contato com você.
                            </S.Description>
                        </>
                    ) : (
                        <>
                            <S.Title $accentColor={accentColor}>
                                Você{' '}
                                <strong>concluiu a contratação</strong>{' '}
                                do seu empréstimo consignado.
                            </S.Title>
                            <S.Description style={{ fontWeight: 500, color: accentColor, marginTop: '24px' }}>
                                Em breve o dinheiro estará disponível na sua conta
                            </S.Description>
                            <S.ActionSection style={{ padding: 0 }}>
                                <Button
                                    clientThemeSettings={clientThemeSettings}
                                    disabled={isDownloading}
                                    onClick={handleDownloadDocuments}
                                >
                                    {isDownloading ? 'Baixando...' : 'Baixar documentos'}
                                </Button>
                            </S.ActionSection>
                        </>
                    )}
                </S.HeroSection>
                {hasAcceptedCard && (
                    <S.ActionSection>
                        <Button
                            clientThemeSettings={clientThemeSettings}
                            disabled={isDownloading}
                            onClick={handleDownloadDocuments}
                        >
                            {isDownloading ? 'Baixando...' : 'Baixar documentos'}
                        </Button>
                    </S.ActionSection>
                )}
            </S.ScrollContent>

            {hasAcceptedCard ? (
                <S.AppBannerSection $accentColor={accentColor}>
                    <S.BannerContent>
                        <S.BannerTextGroup>
                            <S.BannerTitle>Baixe o aplicativo</S.BannerTitle>
                            <S.BannerDescription>
                                <strong>Acompanhe a entrega</strong> e realize a ativação do seu cartão.
                            </S.BannerDescription>
                        </S.BannerTextGroup>
                        {isIOS ? (
                            <S.StoreBadge
                                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                                alt="Download on the App Store"
                                onClick={() => signData.signature_settings.app_store_url && window.open(signData.signature_settings.app_store_url, '_blank')}
                            />
                        ) : (
                            <S.StoreBadge
                                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                                alt="Get it on Google Play"
                                onClick={() => signData.signature_settings.google_play_url && window.open(signData.signature_settings.google_play_url, '_blank')}
                            />
                        )}
                    </S.BannerContent>

                    <S.PoweredByWrapper>
                        <PoweredByQITech />
                    </S.PoweredByWrapper>
                </S.AppBannerSection>
            ) : (
                <S.PlainFooter>
                    <PoweredByQITech />
                </S.PlainFooter>
            )}
        </S.PageWrapper>
    );
};
